import type { ParsedEntry, ParseError, ParseErrorCode, ParseResult } from './parser.model';

export const MAX_AMOUNT = 1_000_000_000_000;
export const MAX_NOTE_LENGTH = 200;

const MULTIPLIERS: Readonly<Record<string, bigint>> = {
  '': 1n,
  rb: 1_000n,
  ribu: 1_000n,
  k: 1_000n,
  jt: 1_000_000n,
  juta: 1_000_000n,
  m: 1_000_000_000n,
};

const AMOUNT_TOKEN = /^(\d[\d.,]*)([a-z]*)$/;
const THOUSANDS_GROUPED = /^\d{1,3}([.,]\d{3})+$/;
const SHORT_DECIMAL = /^(\d+)[.,](\d{1,2})$/;
const AMOUNT_LIKE = /^-?(rp)?\d/i;

const MESSAGES: Readonly<Record<ParseErrorCode, string>> = {
  EMPTY: 'Pesannya kosong. Contoh: makan 25rb',
  MISSING_AMOUNT: 'Nominalnya belum ada. Contoh: makan 25rb atau +gaji 5jt',
  INVALID_AMOUNT: 'Format nominal tidak dikenali. Contoh yang bisa: 25000, 25.000, 25rb, 1.5jt',
  ZERO_AMOUNT: 'Nominal harus lebih dari 0.',
  NEGATIVE_AMOUNT: 'Nominal tidak boleh negatif. Untuk pemasukan, awali dengan +, misalnya +gaji 5jt',
  AMOUNT_TOO_LARGE: 'Nominal terlalu besar. Maksimal Rp1.000.000.000.000.',
};

function fail<T>(code: ParseErrorCode): ParseResult<T> {
  const error: ParseError = { code, message: MESSAGES[code] };
  return { ok: false, error };
}

function digitsToBigInt(numberPart: string, multiplier: bigint): bigint | null {
  if (/^\d+$/.test(numberPart)) return BigInt(numberPart) * multiplier;
  if (THOUSANDS_GROUPED.test(numberPart)) return BigInt(numberPart.replace(/[.,]/g, '')) * multiplier;

  const decimal = SHORT_DECIMAL.exec(numberPart);
  if (decimal && multiplier > 1n) {
    const [, whole = '0', fraction = ''] = decimal;
    const scale = 10n ** BigInt(fraction.length);
    const scaled = (BigInt(whole) * scale + BigInt(fraction)) * multiplier;
    return scaled % scale === 0n ? scaled / scale : null;
  }
  return null;
}

export function parseAmount(raw: string): ParseResult<number> {
  const token = raw.trim().toLowerCase();
  if (token.startsWith('-')) return fail('NEGATIVE_AMOUNT');

  const match = AMOUNT_TOKEN.exec(token.replace(/^rp/, ''));
  if (!match) return fail('INVALID_AMOUNT');

  const [, numberPart = '', suffix = ''] = match;
  const multiplier = MULTIPLIERS[suffix];
  if (multiplier === undefined) return fail('INVALID_AMOUNT');

  const value = digitsToBigInt(numberPart, multiplier);
  if (value === null) return fail('INVALID_AMOUNT');
  if (value === 0n) return fail('ZERO_AMOUNT');
  if (value > BigInt(MAX_AMOUNT)) return fail('AMOUNT_TOO_LARGE');

  return { ok: true, value: Number(value) };
}

export function parseEntry(text: string): ParseResult<ParsedEntry> {
  let body = text.trim();
  const type = body.startsWith('+') ? 'INCOME' : 'EXPENSE';
  if (type === 'INCOME') body = body.slice(1).trim();

  const tokens = body.split(/\s+/).filter((t) => t.length > 0 && t.toLowerCase() !== 'rp');
  if (tokens.length === 0) return fail('EMPTY');

  const amountIndex = tokens.findIndex((t) => AMOUNT_LIKE.test(t));
  if (amountIndex === -1) return fail('MISSING_AMOUNT');

  const amount = parseAmount(tokens[amountIndex] ?? '');
  if (!amount.ok) return amount;

  const words = tokens.filter((_, i) => i !== amountIndex);
  const [hint, ...rest] = words;
  const note = rest.join(' ').slice(0, MAX_NOTE_LENGTH);

  return {
    ok: true,
    value: {
      type,
      amount: amount.value,
      categoryHint: hint ? hint.toLowerCase() : null,
      note: note || null,
    },
  };
}
